import { formatSpan, formatTime } from '../sim/clock.js';
import { currentDevice, glyph } from './glyphs.js';

/**
 * The minimal HUD: what day it is, what time it is, and whether you are on the
 * clock.
 *
 * DOM rather than drawn into the scene. Text in a greybox WebGL scene means
 * either a texture atlas — which is an asset — or a canvas-generated one, which
 * is an asset the project generated rather than downloaded and is still a
 * texture to keep in sync. The DOM already has type, layout and accessibility,
 * it composites for free, and it costs nothing at this scale.
 *
 * Everything here is recomputed from state each frame and written only when the
 * string actually changed. A HUD that touches the DOM sixty times a second for
 * text that changes twice a minute is the cheapest way to make a smooth game
 * stutter.
 */
export function createHud({ parent }) {
  const root = el('div', 'hud');
  const day = el('div', 'hud-day');
  const time = el('div', 'hud-time');
  const shift = el('div', 'hud-shift');
  const state = el('div', 'hud-state');
  const strikes = el('div', 'strikes');
  root.append(day, time, shift, state, strikes);

  /*
   * The one interaction prompt, drawn on the thing it is about: the button in
   * a ring, and what pressing it will do, named ("A: pick up chair"). When the
   * thing has no place on the screen it sits at the bottom instead. Never two:
   * the sentence at the bottom and a tag on the thing were the same prompt
   * said twice, and a new player read them as two.
   */
  const prompt = el('div', 'prompt');
  prompt.hidden = true;
  const promptKey = el('span', 'prompt-key');
  // Read by a screen reader and a script as "A: pick up chair"; not drawn.
  const promptColon = el('span', 'prompt-colon');
  promptColon.textContent = ': ';
  const promptVerb = el('span', 'prompt-verb');
  const promptWhy = el('span', 'prompt-why');
  prompt.append(promptKey, promptColon, promptVerb, promptWhy);

  /*
   * The task card: one thing to do now, in plain words, with how far along it
   * is, and one short line saying why. The guided first day's lessons are said
   * in it too, and a first-time tip is a note under it, so there is only ever
   * one instruction on the screen (`sim/objective.js`, `task`; `sim/tutorial.js`).
   */
  const card = el('div', 'task');
  card.hidden = true;
  const cardKicker = el('div', 'task-kicker');
  const cardMain = el('div', 'task-main');
  const cardDo = el('div', 'task-do');
  const cardProgress = el('div', 'task-progress');
  cardMain.append(cardDo, cardProgress);
  const cardWhy = el('div', 'task-why');
  const cardNote = el('div', 'task-note');
  card.append(cardKicker, cardMain, cardWhy, cardNote);

  // What is in your arms. Bottom left, opposite the objective, because it is
  // the other half of the same question: what am I doing and with what.
  const hands = el('div', 'hands');
  hands.hidden = true;

  // The one thing on screen that has to be readable in peripheral vision: a
  // manager is watching you and you have this long to leave.
  const alarm = el('div', 'alarm');
  alarm.hidden = true;
  const alarmText = el('div', 'alarm-text');
  const alarmBar = el('span', 'alarm-bar');
  const alarmFill = document.createElement('i');
  alarmBar.append(alarmFill);
  alarm.append(alarmText, alarmBar);

  /**
   * Everything else the key could do here, fanned out while it is held.
   *
   * A list rather than a wheel. A radial reads well with a stick and badly with
   * WASD, and the thing being chosen from is a set of sentences of very
   * different lengths — "ride the cart" next to "take back the chair from the
   * near side" — which a wheel has nowhere to put.
   */
  const fan = el('div', 'fan');
  fan.hidden = true;

  /**
   * The fan and the prompt, stacked, in that order.
   *
   * One column anchored to the bottom of the screen rather than two things
   * positioned near each other. They used to be two: the prompt at twelve
   * percent from the bottom and the fan at eighty-four pixels, which are the
   * same place on a short window and different places on a tall one, and on
   * every window a menu of four or more rows grew up through the prompt it had
   * just come out of. A column cannot do that however many rows there are.
   */
  const stack = el('div', 'stack');
  stack.append(fan);

  const toast = el('div', 'toast');
  toast.hidden = true;
  let toastUntil = 0;


  /*
   * Where the objective is, on the screen: a gold diamond over it with the
   * distance under, or, when it is behind you or out of view, pinned to the
   * edge of the screen as an arrow pointing the way to turn.
   */
  const marker = el('div', 'marker');
  marker.hidden = true;
  const markerIcon = el('span', 'marker-icon');
  const markerText = el('span', 'marker-text');
  marker.append(markerIcon, markerText);


  // Pins over places and people worth walking to: a class, a mission.
  const pinLayer = el('div', 'pins');

  parent.append(root, card, hands, alarm, stack, prompt, toast, marker, pinLayer);

  const last = new Map();
  function set(node, text, className) {
    // Keys named for whichever device is in use. See `glyphs.js`.
    text = glyph(text);
    if (last.get(node) !== text) {
      node.textContent = text;
      last.set(node, text);
    }
    if (className !== undefined && node.dataset.tone !== className) {
      node.dataset.tone = className;
    }
  }

  return {
    root,

    /** Hidden while a modal owns the screen; it would ghost through otherwise. */
    setVisible(visible) {
      root.hidden = !visible;
      if (!visible) {
        prompt.hidden = true;
        hands.hidden = true;
        alarm.hidden = true;
        marker.hidden = true;
        pinLayer.replaceChildren();
        /*
         * The toast too. It is a sibling of `root` rather than a child of it,
         * so hiding the HUD never hid it and a line said just before a panel
         * opened sat on top of that panel for the rest of its four seconds.
         * Rare until something said a line and then sent you to the board,
         * which is exactly what passing a class does.
         */
        toast.hidden = true;
      }
    },

    /** The write-up tally: one tick per strike used, and nothing while there are none. */
    setStrikes(count, max) {
      strikes.hidden = count <= 0;
      if (strikes.childElementCount !== max) {
        strikes.replaceChildren(
          ...Array.from({ length: max }, () => document.createElement('i'))
        );
      }
      [...strikes.children].forEach((tick, index) => {
        tick.dataset.struck = index < count ? 'yes' : 'no';
      });
    },

    /**
     * The held-key fan. Passing null puts it away.
     *
     * Rebuilt only when the list or the selection changes: a menu that rewrites
     * itself sixty times a second for text that changes when you press a key is
     * the cheapest way to make a smooth game stutter.
     */
    /**
     * The menu, with the key that picks each row in its own column.
     *
     * Every row gets a number, including the ones that are refused, because a
     * refused row still has something to say when you press it and a menu that
     * silently skips a number is a menu you cannot trust to be complete. The
     * numbers are in their own span rather than in the sentence so they line up
     * down the left of the list, which is the only reason you can find the one
     * you want without reading all of them.
     */
    setFan(state) {
      if (!state) {
        if (!fan.hidden) {
          fan.hidden = true;
          fan.replaceChildren();
          last.delete(fan);
        }
        return;
      }
      const wheel = Boolean(state.wheel);
      const signature = `${currentDevice()}|${wheel}|${state.at}|${state.rows.map((row) => `${row.key}${row.label}`).join('|')}`;
      if (last.get(fan) === signature) return;
      last.set(fan, signature);
      fan.hidden = false;
      fan.dataset.wheel = wheel ? 'yes' : 'no';
      if (wheel) {
        /*
         * On a pad the fan is a wheel: the rows round a ring, the first at the
         * top and on clockwise, the one the left stick points at lit, and its
         * whole sentence in the middle, because a ring has room for a short
         * label and not for "take back the chair from the near side".
         */
        const n = state.rows.length;
        const ring = el('div', 'wheel');
        const centre = el('div', 'wheel-centre');
        const chosen = state.rows[state.at];
        centre.textContent = chosen ? glyph(chosen.label) : '';
        ring.append(centre);
        state.rows.forEach((entry, index) => {
          const angle = (index / n) * Math.PI * 2;
          const slice = el('div', 'wheel-slice');
          slice.style.left = `${50 + Math.sin(angle) * 40}%`;
          slice.style.top = `${50 - Math.cos(angle) * 40}%`;
          slice.textContent = glyph(entry.short ?? entry.label);
          slice.dataset.on = index === state.at ? 'yes' : 'no';
          slice.dataset.blocked = entry.blocked ? 'yes' : 'no';
          ring.append(slice);
        });
        const hint = el('div', 'wheel-hint');
        hint.textContent = 'Left stick to choose  ·  let go of A  ·  B to back out';
        fan.replaceChildren(ring, hint);
        return;
      }
      fan.replaceChildren(
        ...state.rows.map((entry, index) => {
          const row = el('div', 'fan-row');
          const key = el('span', 'fan-key');
          // A pad has no number keys: the row under the d-pad is the one A
          // lets go on, and it is marked as such.
          key.textContent = entry.key == null ? (index === state.at ? glyph('[E]') : '') : `[${entry.key}]`;
          const label = el('span', 'fan-label');
          label.textContent = glyph(entry.label);
          row.append(key, label);
          row.dataset.on = index === state.at ? 'yes' : 'no';
          row.dataset.blocked = entry.blocked ? 'yes' : 'no';
          return row;
        })
      );
    },

    /** "A manager can see you." Passing null clears it. */
    setAlarm(alarmState) {
      if (!alarmState) {
        alarm.hidden = true;
        return;
      }
      alarm.hidden = false;
      set(alarmText, alarmState.text);
      alarmFill.style.width = `${Math.round(Math.min(alarmState.progress, 1) * 100)}%`;
    },

    /**
     * What you are carrying, and how hard you are about to throw it.
     *
     * The wind-up bar is in the same block as the load on purpose: they are the
     * same question. A short pull puts it down, a long one throws it.
     */
    setHands(load, aim = null) {
      if (!load || !load.count) {
        hands.hidden = true;
        return;
      }
      hands.hidden = false;
      const bar = aim
        ? `<span class="wind"><i style="width:${Math.round(aim.power * 100)}%"></i></span>`
        : '';
      hands.innerHTML = `<b>${load.count} × ${load.name}</b> <span>of ${load.capacity}${
        load.via ? ` · ${load.via}` : ''
      }</span>${bar}`;
    },

    update(clock, status, now = performance.now()) {
      root.hidden = false;
      set(day, `DAY ${clock.day} · ${clock.dayName.toUpperCase()}`);
      set(time, formatTime(clock.minutes));
      set(shift, `${status.shift.name} · ${status.shift.start}–${status.shift.end}`);
      set(state, ...phraseFor(status));
      /*
       * Only the time while you are working: the day, the shift and how long
       * is left on it say themselves before you clock in, after you clock
       * out, and in the shift's last half hour. Nothing on the screen that is
       * not needed right now.
       */
      const quiet = status.phase === 'working' && !(status.untilEnd <= 30);
      day.hidden = quiet;
      shift.hidden = quiet;
      state.hidden = quiet;
      root.dataset.quiet = quiet ? 'yes' : 'no';

      if (toastUntil && now > toastUntil) {
        toast.hidden = true;
        toastUntil = 0;
      }
    },

    /**
     * The task card: `{ kicker, do, progress, why, note, tone, kind }`, or null.
     * `kind` is 'lesson', 'tip' or 'task', for its border.
     */
    setTask(state) {
      if (!state || !state.do) {
        card.hidden = true;
        return;
      }
      card.hidden = false;
      card.dataset.kind = state.kind ?? 'task';
      set(cardKicker, state.kicker ?? '');
      cardKicker.hidden = !state.kicker;
      set(cardDo, state.do, state.tone ?? 'ready');
      set(cardProgress, state.progress ?? '');
      cardProgress.hidden = !state.progress;
      set(cardWhy, state.why ?? '');
      cardWhy.hidden = !state.why;
      set(cardNote, state.note ?? '');
      cardNote.hidden = !state.note;
    },

    /**
     * The one prompt: `{ key, verb, blocked, x, y }`, or null. `key` is in
     * keyboard words (`[E]`, `[R]`, `[C]`) and named for the device here;
     * `blocked` is why it will not, said after the verb, dimmed. With `x` and
     * `y` (screen pixels) it sits on the thing, otherwise at the bottom.
     */
    setPrompt(state) {
      if (!state) {
        prompt.hidden = true;
        return;
      }
      prompt.hidden = false;
      set(promptKey, glyph(state.key ?? '[E]').replace(/^\[|\]$/g, ''));
      set(promptVerb, state.verb ?? '');
      set(promptWhy, state.blocked ? `  ·  ${state.blocked}` : '');
      promptWhy.hidden = !state.blocked;
      prompt.dataset.blocked = state.blocked ? 'yes' : 'no';
      const on = Number.isFinite(state.x) && Number.isFinite(state.y);
      prompt.dataset.on = on ? 'object' : 'bottom';
      prompt.style.transform = on ? `translate(${Math.round(state.x)}px, ${Math.round(state.y)}px) translate(-50%, -100%)` : '';
    },

    /**
     * The objective marker: `{ x, y, edge, angle, distance }` in screen pixels
     * (`angle` the way the arrow points when pinned to the edge), or null.
     */
    setMarker(state) {
      if (!state) {
        marker.hidden = true;
        return;
      }
      marker.hidden = false;
      marker.dataset.edge = state.edge ? 'yes' : 'no';
      set(markerText, state.distance);
      markerIcon.style.transform = state.edge ? `rotate(${state.angle}rad)` : '';
      marker.style.transform = `translate(${Math.round(state.x)}px, ${Math.round(state.y)}px) translate(-50%, -50%)`;
    },

    /**
     * Pins over places and people: `[{ x, y, kind, label, title, edge }]` in
     * screen pixels. A class is a labelled blue pin; a mission a gold "!" over
     * the person, with their name under it.
     */
    setPins(list) {
      while (pinLayer.children.length > list.length) pinLayer.lastChild.remove();
      while (pinLayer.children.length < list.length) {
        const pin = el('div', 'pin');
        pin.append(el('span', 'pin-label'), el('span', 'pin-title'));
        pinLayer.append(pin);
      }
      list.forEach((entry, i) => {
        const pin = pinLayer.children[i];
        pin.dataset.kind = entry.kind;
        pin.dataset.edge = entry.edge ? 'yes' : 'no';
        set(pin.children[0], entry.label);
        set(pin.children[1], entry.title ?? '');
        pin.children[1].hidden = !entry.title;
        pin.style.transform = `translate(${Math.round(entry.x)}px, ${Math.round(entry.y)}px) translate(-50%, -100%)`;
      });
    },

    /** A line that matters for a few seconds: punched in, wrote you up, graded. */
    say(text, { tone = 'info', seconds = 4, now = performance.now() } = {}) {
      toast.hidden = false;
      toast.dataset.tone = tone;
      toast.textContent = glyph(text);
      toastUntil = now + seconds * 1000;
    },
  };
}

/** The one line that says where you stand with the department right now. */
function phraseFor(status) {
  switch (status.phase) {
    case 'before':
      return [`Off duty · clock opens ${formatTime(status.opens)}`, 'idle'];
    case 'open':
      return status.untilStart >= 0
        ? [`Clock in · starts in ${formatSpan(status.untilStart)}`, 'warn']
        : [`LATE · ${formatSpan(-status.untilStart)} past start`, 'bad'];
    case 'working':
      return [`On the clock · ${formatSpan(status.untilEnd)} left`, 'good'];
    case 'over':
      return ['Off the clock', 'idle'];
    case 'missed':
      return ['NO SHOW', 'bad'];
    case 'dismissed':
      return ['DISMISSED', 'bad'];
    default:
      return ['', 'idle'];
  }
}

function el(tag, className) {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}
